import React, { useState, useEffect, useMemo } from 'react';
import { ShoppingBag, ArrowRight, X, ZoomIn, ArrowLeft, Search } from 'lucide-react';
import { getBoutiqueProducts, createBoutiqueOrder } from '../utils/api';
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';
import SEO, { SEO_CONFIG } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useCurrency } from '../contexts/CurrencyContext';
import { getImageUrl, getPlaceholderImage } from '../utils/imageUtils';

const BoutiquePage = () => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem('cart') || '[]'));
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [customerInfo, setCustomerInfo] = useState({
    name: '', email: '', phone: '',
    delivery_address: '', delivery_city: '', delivery_state: '', delivery_country: 'Nigeria', delivery_notes: ''
  });
  
  // Size selection modal state
  const [showSizeModal, setShowSizeModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  
  // Default size options (used when admin hasn't set available_sizes)
  const DEFAULT_MALE_SIZES = ['S', 'M', 'L', 'XL', '2XL'];
  const DEFAULT_FEMALE_SIZES = ['6', '8', '10', '12', '14'];
  const DEFAULT_CHILD_SIZES = ['2', '4', '6', '8', '10'];
  
  // Get available sizes for a product (admin-defined or defaults, NO "Other" option)
  const getAvailableSizes = (product) => {
    // If admin has set available_sizes, use those
    if (product.available_sizes && product.available_sizes.length > 0) {
      return product.available_sizes;
    }
    // Otherwise use defaults based on gender
    if (product.gender === 'female') return DEFAULT_FEMALE_SIZES;
    if (product.gender === 'child') return DEFAULT_CHILD_SIZES;
    return DEFAULT_MALE_SIZES;
  };

  // Category filters
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedAudience, setSelectedAudience] = useState('all');
  const [selectedGender, setSelectedGender] = useState('all');

  const CATEGORIES = [
    { value: 'all', label: 'All Items' },
    { value: 'traditional', label: 'Cultural Wears' },
    { value: 'modern', label: 'Modern Wear' },
    { value: 'blanks', label: 'Blanks' },
  ];

  const AUDIENCES = [
    { value: 'all', label: 'All' },
    { value: 'adults', label: 'Adults' },
    { value: 'kids', label: 'Kids' },
  ];

  const GENDERS = [
    { value: 'all', label: 'All' },
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'unisex', label: 'Unisex' },
  ];

  useEffect(() => {
    loadProducts();
    const savedCart = localStorage.getItem('cart');
    if (savedCart) setCart(JSON.parse(savedCart));
  }, []);

  const loadProducts = async () => {
    try {
      const response = await getBoutiqueProducts();
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // Filter products with search
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchCategory = selectedCategory === 'all' || product.style === selectedCategory;
      const matchAudience = selectedAudience === 'all' || product.audience === selectedAudience;
      const matchGender = selectedGender === 'all' || product.gender === selectedGender;
      
      // Search filter (case insensitive, partial match)
      const searchLower = searchQuery.toLowerCase().trim();
      const matchSearch = !searchLower || 
        product.name?.toLowerCase().includes(searchLower) ||
        product.style?.toLowerCase().includes(searchLower) ||
        product.color?.toLowerCase().includes(searchLower) ||
        product.tags?.some(tag => tag.toLowerCase().includes(searchLower));
      
      return matchCategory && matchAudience && matchGender && matchSearch;
    });
  }, [products, selectedCategory, selectedAudience, selectedGender, searchQuery]);

  // Handle add to cart - show size selection modal for clothing items
  const handleAddToCart = (product) => {
    // Check if product needs size selection (has gender attribute)
    if (product.gender === 'male' || product.gender === 'female' || product.gender === 'child' || product.gender === 'unisex') {
      setSelectedProduct(product);
      const availableSizes = getAvailableSizes(product);
      setSelectedSize(availableSizes[0] || '');
      setShowSizeModal(true);
    } else {
      // No size needed, add directly
      addToCartWithSize(product, null);
    }
  };

  const addToCartWithSize = (product, size) => {
    const cartItemId = size ? `${product.id}-${size}` : product.id;
    const existingItem = cart.find(item => item.cartItemId === cartItemId || (item.id === product.id && item.size === size));
    
    let newCart;
    if (existingItem) {
      newCart = cart.map(item => 
        (item.cartItemId === cartItemId || (item.id === product.id && item.size === size))
          ? { ...item, quantity: item.quantity + 1 } 
          : item
      );
    } else {
      newCart = [...cart, { 
        ...product, 
        cartItemId,
        size: size,
        quantity: 1 
      }];
    }
    
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
    window.dispatchEvent(new Event('cartUpdated'));
    toast.success(size ? `Added to cart (Size: ${size})` : 'Added to cart!');
    setShowSizeModal(false);
    setSelectedProduct(null);
  };

  const addToCart = (product) => {
    handleAddToCart(product);
  };

  const removeFromCart = (cartItemId) => {
    const newCart = cart.filter(item => (item.cartItemId || item.id) !== cartItemId);
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
    toast.success('Removed from cart');
  };

  const getTotalPrice = () => cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (!customerInfo.name.trim() || !customerInfo.email.includes('@') || !customerInfo.phone.trim() || !customerInfo.delivery_address.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCheckoutLoading(true);
    try {
      const formData = new FormData();
      formData.append('customer_name', customerInfo.name);
      formData.append('customer_email', customerInfo.email);
      formData.append('customer_phone', customerInfo.phone);
      formData.append('cart_items', JSON.stringify(cart));
      formData.append('delivery_address', customerInfo.delivery_address);
      formData.append('delivery_city', customerInfo.delivery_city || '');
      formData.append('delivery_state', customerInfo.delivery_state || '');
      formData.append('delivery_notes', customerInfo.delivery_notes || '');

      const response = await createBoutiqueOrder(formData);
      toast.success('Order created successfully!');
      localStorage.removeItem('cart');
      setCart([]);
      setShowCheckoutModal(false);
      navigate(`/order-summary/${response.data.order_id || response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D90429]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <SEO 
        title={SEO_CONFIG.boutique.title}
        description={SEO_CONFIG.boutique.description}
        image={SEO_CONFIG.boutique.image}
        url={SEO_CONFIG.boutique.url}
      />

      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex justify-between items-center">
            <div>
              <button onClick={() => navigate('/')} className="flex items-center text-zinc-500 hover:text-zinc-900 mb-2 text-sm">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to Home
              </button>
              <h1 className="text-3xl md:text-4xl font-bold text-zinc-900" data-testid="boutique-title">{SEO_CONFIG.boutique.h1}</h1>
              <p className="text-zinc-600 mt-1">Shop our curated collection</p>
            </div>
            <button
              onClick={() => document.getElementById('cart-section').scrollIntoView({ behavior: 'smooth' })}
              className="flex items-center gap-2 bg-[#D90429] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#B90322] transition-colors"
              data-testid="view-cart-btn"
            >
              <ShoppingBag size={20} />
              Cart ({cart.length})
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Category Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <Link to="/boutique/nigerian" className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div className="aspect-[16/9] bg-gradient-to-br from-[#D90429] to-pink-600 relative">
              <img src="https://images.unsplash.com/photo-1617627143750-d86bc21e425a?w=800&q=80" alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-90 transition-opacity group-hover:scale-105 duration-500" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Nigerian Traditional Wear</h2>
                <p className="text-white/90 mb-3">Agbada, Senator Wear, Kaftan & More</p>
                <span className="flex items-center gap-2 text-white font-medium">Shop Now <ArrowRight size={18} /></span>
              </div>
            </div>
          </Link>

          <Link to="/boutique/modern" className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all">
            <div className="aspect-[16/9] bg-gradient-to-br from-zinc-800 to-zinc-600 relative">
              <img src="https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=800&q=80" alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-90 transition-opacity group-hover:scale-105 duration-500" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">Modern Wear</h2>
                <p className="text-white/90 mb-3">T-Shirts, Polos, Hoodies & More</p>
                <span className="flex items-center gap-2 text-white font-medium">Shop Now <ArrowRight size={18} /></span>
              </div>
            </div>
          </Link>
        </div>

        {/* Search Bar */}
        <div className="flex justify-center mb-6">
          <div className="relative w-full max-w-md sm:max-w-lg md:max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
            <input
              type="text"
              placeholder="Search boutique items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-10 py-3 border border-zinc-200 rounded-full focus:ring-2 focus:ring-[#D90429] focus:border-transparent bg-white shadow-sm"
              data-testid="boutique-search"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-8">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-zinc-600">Style:</span>
              <div className="flex gap-1">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.value}
                    onClick={() => setSelectedCategory(cat.value)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                      selectedCategory === cat.value
                        ? 'bg-[#D90429] text-white'
                        : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                    }`}
                    data-testid={`filter-${cat.value}`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Audience Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-zinc-600">For:</span>
              <div className="flex gap-1">
                {AUDIENCES.map(aud => (
                  <button
                    key={aud.value}
                    onClick={() => setSelectedAudience(aud.value)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                      selectedAudience === aud.value
                        ? 'bg-[#D90429] text-white'
                        : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                    }`}
                    data-testid={`filter-aud-${aud.value}`}
                  >
                    {aud.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Gender Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-zinc-600">Gender:</span>
              <div className="flex gap-1">
                {GENDERS.map(gen => (
                  <button
                    key={gen.value}
                    onClick={() => setSelectedGender(gen.value)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                      selectedGender === gen.value
                        ? 'bg-[#D90429] text-white'
                        : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                    }`}
                    data-testid={`filter-gen-${gen.value}`}
                  >
                    {gen.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Results count */}
            <div className="ml-auto text-sm text-zinc-500">
              {filteredProducts.length} products
            </div>
          </div>
        </div>

        {/* No Results Message */}
        {filteredProducts.length === 0 && !loading && (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm mb-8">
            <p className="text-zinc-500 text-lg">No items found. Try another keyword.</p>
            <button
              onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}
              className="mt-4 text-[#D90429] hover:underline"
            >
              Clear filters
            </button>
          </div>
        )}

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {filteredProducts.map(product => (
              <Card key={product.id} className="group overflow-hidden hover:shadow-lg transition-all" data-testid="product-card">
                {/* ISSUE 2 FIX: Use object-contain to show full outfit without cropping */}
                <div className="relative aspect-square cursor-pointer overflow-hidden bg-white flex items-center justify-center" onClick={() => setLightboxImage(product)}>
                  <img src={getImageUrl(product.image_url)} alt={product.name} className="w-full h-full object-contain object-center transition-transform duration-500 group-hover:scale-110" onError={(e) => { e.target.src = getPlaceholderImage(product.name); }} />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                    <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={40} />
                  </div>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-zinc-900 mb-1">{product.name}</h3>
                  <p className="text-sm text-zinc-500 mb-3 line-clamp-2">{product.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-bold text-[#D90429]">{formatPrice(product.price)}</span>
                    <Button onClick={() => addToCart(product)} size="sm" className="bg-[#D90429] hover:bg-[#B90322]" data-testid={`add-to-cart-${product.id}`}>
                      Add to Cart
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 mb-16">
            <p className="text-zinc-500 text-lg">No products found matching your filters.</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => {
                setSelectedCategory('all');
                setSelectedAudience('all');
                setSelectedGender('all');
              }}
            >
              Clear Filters
            </Button>
          </div>
        )}

        {/* Cart Section */}
        <Card id="cart-section" className="mb-8">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-2xl font-bold text-zinc-900 mb-6">Shopping Cart</h2>

            {cart.length === 0 ? (
              <p className="text-center text-zinc-500 py-8">Your cart is empty</p>
            ) : (
              <>
                <div className="space-y-4 mb-6">
                  {cart.map(item => (
                    <div key={item.cartItemId || item.id} className="flex justify-between items-center border-b pb-4" data-testid="cart-item">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center">
                          <img src={getImageUrl(item.image_url)} alt={item.name} className="max-w-full max-h-full object-contain" />
                        </div>
                        <div>
                          <p className="font-semibold text-zinc-900">{item.name}</p>
                          {item.size && (
                            <p className="text-xs text-zinc-500">Size: {item.size}</p>
                          )}
                          <p className="text-sm text-zinc-500">Qty: {item.quantity}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[#D90429]">{formatPrice(item.price * item.quantity)}</p>
                        <button onClick={() => removeFromCart(item.cartItemId || item.id)} className="text-sm text-red-500 hover:underline" data-testid={`remove-from-cart-${item.id}`}>Remove</button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-6">
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-xl font-semibold">Total:</span>
                    <span className="text-3xl font-bold text-[#D90429]">{formatPrice(getTotalPrice())}</span>
                  </div>
                  <Button onClick={() => setShowCheckoutModal(true)} disabled={checkoutLoading} className="w-full bg-[#D90429] hover:bg-[#B90322] py-6 text-lg rounded-full" data-testid="checkout-btn">
                    {checkoutLoading ? 'Processing...' : 'Proceed to Checkout'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Checkout Modal */}
      {showCheckoutModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6 md:p-8">
              <h2 className="text-2xl font-bold mb-6">Delivery & Contact Information</h2>
              <form onSubmit={handleSubmitOrder} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Full Name *</label>
                    <input type="text" value={customerInfo.name} onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})} placeholder="John Doe" className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429]" required data-testid="boutique-customer-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email *</label>
                    <input type="email" value={customerInfo.email} onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})} placeholder="john@example.com" className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429]" required data-testid="boutique-customer-email" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Phone *</label>
                    <input type="tel" value={customerInfo.phone} onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})} placeholder="+234 800 000 0000" className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429]" required data-testid="boutique-customer-phone" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Delivery Address *</label>
                    <textarea value={customerInfo.delivery_address} onChange={(e) => setCustomerInfo({...customerInfo, delivery_address: e.target.value})} placeholder="Enter your full delivery address" rows={3} className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429]" required data-testid="boutique-delivery-address" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">City</label>
                    <input type="text" value={customerInfo.delivery_city} onChange={(e) => setCustomerInfo({...customerInfo, delivery_city: e.target.value})} placeholder="Lagos" className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429]" data-testid="boutique-delivery-city" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">State / Province</label>
                    <input type="text" value={customerInfo.delivery_state} onChange={(e) => setCustomerInfo({...customerInfo, delivery_state: e.target.value})} placeholder="Lagos State" className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429]" data-testid="boutique-delivery-state" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Country</label>
                    <input type="text" value={customerInfo.delivery_country} onChange={(e) => setCustomerInfo({...customerInfo, delivery_country: e.target.value})} placeholder="Nigeria" className="w-full px-4 py-3 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-[#D90429]" data-testid="boutique-delivery-country" />
                  </div>
                </div>

                <div className="bg-zinc-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold">Total:</span>
                    <span className="text-2xl font-bold text-[#D90429]">{formatPrice(getTotalPrice())}</span>
                  </div>
                  <p className="text-sm text-zinc-500">You'll be redirected to payment instructions after submitting.</p>
                </div>

                <div className="flex gap-4 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCheckoutModal(false)} className="flex-1" data-testid="cancel-checkout-btn">Cancel</Button>
                  <Button type="submit" disabled={checkoutLoading} className="flex-1 bg-[#D90429] hover:bg-[#B90322]" data-testid="submit-boutique-order-btn">
                    {checkoutLoading ? 'Submitting...' : 'Submit Order'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setLightboxImage(null)}>
          <button onClick={() => setLightboxImage(null)} className="absolute top-4 right-4 text-white hover:text-zinc-300 bg-black/50 rounded-full p-2"><X size={32} /></button>
          <div className="max-w-5xl max-h-[90vh] relative" onClick={(e) => e.stopPropagation()}>
            <img src={lightboxImage.image_url} alt={lightboxImage.name} className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" />
            <Card className="mt-4">
              <CardContent className="p-4">
                <h3 className="text-2xl font-bold mb-2">{lightboxImage.name}</h3>
                <p className="text-zinc-600 mb-3">{lightboxImage.description}</p>
                <div className="flex justify-between items-center">
                  <span className="text-3xl font-bold text-[#D90429]">{formatPrice(lightboxImage.price)}</span>
                  <Button onClick={(e) => { e.stopPropagation(); addToCart(lightboxImage); setLightboxImage(null); }} className="bg-[#D90429] hover:bg-[#B90322]">Add to Cart</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Size Selection Modal */}
      {showSizeModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <Card className="max-w-md w-full">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold">{selectedProduct.name}</h3>
                  <p className="text-[#D90429] font-semibold">{formatPrice(selectedProduct.price)}</p>
                </div>
                <button onClick={() => setShowSizeModal(false)} className="text-zinc-400 hover:text-zinc-600">
                  <X size={24} />
                </button>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-zinc-700 mb-2">Select Size</label>
                <div className="grid grid-cols-3 gap-2">
                  {getAvailableSizes(selectedProduct).map(size => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`py-3 px-4 border rounded-lg text-center font-medium transition-all ${
                        selectedSize === size 
                          ? 'border-[#D90429] bg-red-50 text-[#D90429]' 
                          : 'border-zinc-200 hover:border-zinc-300'
                      }`}
                      data-testid={`size-option-${size}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
              
              <Button
                onClick={() => addToCartWithSize(selectedProduct, selectedSize)}
                disabled={!selectedSize}
                className="w-full bg-[#D90429] hover:bg-[#B90322]"
                data-testid="confirm-size-btn"
              >
                Add to Cart
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BoutiquePage;
